import { create } from "zustand";
import type { GroupLesson, PrivateLesson, GroupSignup } from "@/types";
import { lessonsApi, emailApi, notificationsApi, n8nApi } from "@/lib/api";
import { supabase } from "@/lib/supabase";
import {
  getReservationConfirmationTemplate,
  getCancellationConfirmationTemplate,
  getPrivateLessonRequestTemplate,
} from "@/lib/utils/email-templates";

function toErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "Unknown error";
}

/**
 * Lesson Store
 * Správa skupinových a súkromných lekcií, prihlášok pomocou Supabase
 */

interface LessonStore {
  // State
  groupLessons: GroupLesson[];
  privateLessons: PrivateLesson[];
  upcomingLessons: GroupLesson[];
  mySignups: GroupSignup[];
  selectedLesson: GroupLesson | null;
  isLoading: boolean;
  error: string | null;

  // Actions
  fetchGroupLessons: (filters?: Record<string, unknown>) => Promise<void>;
  fetchPrivateLessons: (
    userId: string,
    filters?: Record<string, unknown>,
  ) => Promise<void>;
  fetchUpcomingLessons: () => Promise<void>;
  fetchMySignups: (userId: string) => Promise<void>;
  selectLesson: (lesson: GroupLesson | null) => void;
  bookLesson: (lessonId: number, pairId?: number) => Promise<boolean>;
  cancelBooking: (signupId: number) => Promise<boolean>;
  requestPrivateLesson: (options: {
    userId: string;
    startAt: string;
    durationHours: number;
    lessonType: "single" | "pair";
    pairId?: number;
    availabilitySlotId?: number;
    danceStyle?: string;
    additionalNotes?: string;
  }) => Promise<boolean>;
  clearLessons: () => void;

  // Realtime callbacks
  handleLessonChange: () => void;
  handleSignupChange: () => void;
  handlePrivateLessonChange: () => void;
}

export const useLessonStore = create<LessonStore>((set, get) => ({
  // Initial state
  groupLessons: [],
  privateLessons: [],
  upcomingLessons: [],
  mySignups: [],
  selectedLesson: null,
  isLoading: false,
  error: null,

  /**
   * Načítať skupinové lekcie s filtrami
   */
  fetchGroupLessons: async (filters?: Record<string, unknown>) => {
    set({ isLoading: true, error: null });
    try {
      const response = await lessonsApi.list(filters);

      if (response.success && response.data) {
        set({ groupLessons: response.data });
      } else {
        set({
          error: response.error ? response.error : "Failed to fetch lessons",
        });
      }
    } catch (error) {
      set({ error: toErrorMessage(error) });
    } finally {
      set({ isLoading: false });
    }
  },

  /**
   * Načítať súkromné lekcie pre používateľa
   */
  fetchPrivateLessons: async (
    userId: string,
    filters?: Record<string, unknown>,
  ) => {
    set({ isLoading: true, error: null });
    try {
      let query = supabase
        .from("private_lessons")
        .select("*")
        .eq("user_id", userId);

      if (filters?.status) {
        query = query.eq("status", filters.status);
      }

      query = query.order("start_at", { ascending: false });

      const { data, error } = await query;

      if (error) throw error;

      set({ privateLessons: (data as PrivateLesson[]) || [] });
    } catch (error) {
      set({ error: toErrorMessage(error) });
    } finally {
      set({ isLoading: false });
    }
  },

  /**
   * Načítať budúce lekcie
   */
  fetchUpcomingLessons: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await lessonsApi.list({
        upcoming: true,
        status: "scheduled",
      });

      if (response.success && response.data) {
        set({ upcomingLessons: response.data });
      } else {
        set({
          error: response.error
            ? response.error
            : "Failed to fetch upcoming lessons",
        });
      }
    } catch (error) {
      set({ error: toErrorMessage(error) });
    } finally {
      set({ isLoading: false });
    }
  },

  /**
   * Načítať moje prihlášky
   */
  fetchMySignups: async (userId: string) => {
    set({ isLoading: true, error: null });
    try {
      const { data, error } = await supabase
        .from("group_signups")
        .select("*")
        .eq("user_id", userId)
        .order("created_at", { ascending: false });

      if (error) throw error;

      set({ mySignups: (data as GroupSignup[]) || [] });
    } catch (error) {
      set({ mySignups: [], error: toErrorMessage(error) });
    } finally {
      set({ isLoading: false });
    }
  },

  /**
   * Vybrať lekciu (pre detail view)
   */
  selectLesson: (lesson: GroupLesson | null) => {
    set({ selectedLesson: lesson });
  },

  /**
   * Prihlásiť sa na lekciu
   */
  bookLesson: async (lessonId: number, pairId?: number) => {
    set({ isLoading: true, error: null });
    const finish = () => set({ isLoading: false });
    try {
      const response = await lessonsApi.book(lessonId, pairId);

      if (response.success) {
        // Refresh lessons and signups
        await get().fetchUpcomingLessons();
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (user) {
          await get().fetchMySignups(user.id);

          // Send email notification to dancer and admin
          const lesson = get().groupLessons.find((l) => l.id === lessonId);
          if (lesson && user.email) {
            // Get user profile for display name
            const { data: userProfile } = await supabase
              .from("users")
              .select("display_name")
              .eq("id", user.id)
              .single();

            // Get admin email
            const { data: adminData } = await supabase
              .from("users")
              .select("email")
              .eq("role", "admin")
              .single();

            const userName =
              userProfile?.display_name || user.email.split("@")[0];
            const lessonDate = new Date(lesson.start_at).toLocaleDateString(
              "sk-SK",
            );
            const lessonTime = new Date(lesson.start_at).toLocaleTimeString(
              "sk-SK",
              {
                hour: "2-digit",
                minute: "2-digit",
              },
            );

            const emailHtml = getReservationConfirmationTemplate({
              userName: userName,
              lessonTitle: lesson.title,
              lessonDate: lessonDate,
              lessonTime: lessonTime,
              lessonType: "group",
            });

            const emailResult = await emailApi.sendEmail({
              to: user.email,
              subject: `Potvrdenie rezervácie lekcie: ${lesson.title}`,
              html: emailHtml,
              user_id: user.id,
              cc: adminData?.email ? [adminData.email] : [],
            });

            if (emailResult.success) {
              console.log(
                "Reservation confirmation email sent:",
                emailResult.message_id,
              );
            } else {
              console.error(
                "Failed to send reservation confirmation email:",
                emailResult.error,
              );
            }

            // Create notification
            await notificationsApi.create({
              user_id: user.id,
              type: "lesson_reservation",
              title: "Rezervácia lekcie potvrdená",
              message: `Vaša rezervácia pre lekciu "${lesson.title}" bola potvrdená. Dátum: ${lessonDate}, čas: ${lessonTime}`,
              data: {
                lesson_id: lessonId,
                lesson_title: lesson.title,
                lesson_date: lesson.start_at,
              },
              sendEmail: false, // Email už odoslaný cez emailApi
            });
          }
        }
        finish();
        return true;
      }
      const errMsg = response.error ? response.error : "Failed to book lesson";
      set({ error: errMsg });
      finish();
      return false;
    } catch (error) {
      set({ error: toErrorMessage(error) });
      finish();
      return false;
    }
  },

  /**
   * Zrušiť prihlášku
   */
  cancelBooking: async (signupId: number) => {
    set({ isLoading: true, error: null });
    const finish = () => set({ isLoading: false });
    try {
      // First, get the signup and lesson details before cancelling
      const { data: signupData, error: signupError } = await supabase
        .from("group_signups")
        .select("*, group_lessons(*)")
        .eq("id", signupId)
        .single();

      if (signupError) throw signupError;
      if (!signupData) throw new Error("Signup not found");

      const lesson = signupData.group_lessons as GroupLesson;

      // Now cancel the signup
      const { error } = await supabase
        .from("group_signups")
        .update({ status: "cancelled" })
        .eq("id", signupId);

      if (error) throw error;

      // Refresh signups
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (user) {
        await get().fetchMySignups(user.id);

        // Send email notification to dancer and admin
        if (lesson && user.email) {
          // Get user profile for display name
          const { data: userProfile } = await supabase
            .from("users")
            .select("display_name")
            .eq("id", user.id)
            .single();

          // Get admin email
          const { data: adminData } = await supabase
            .from("users")
            .select("email")
            .eq("role", "admin")
            .single();

          const userName =
            userProfile?.display_name || user.email.split("@")[0];
          const lessonDate = new Date(lesson.start_at).toLocaleDateString(
            "sk-SK",
          );
          const lessonTime = new Date(lesson.start_at).toLocaleTimeString(
            "sk-SK",
            {
              hour: "2-digit",
              minute: "2-digit",
            },
          );

          const emailHtml = getCancellationConfirmationTemplate({
            userName: userName,
            lessonTitle: lesson.title,
            lessonDate: lessonDate,
            lessonTime: lessonTime,
            lessonType: "group",
          });

          const emailResult = await emailApi.sendEmail({
            to: user.email,
            subject: `Zrušenie rezervácie lekcie: ${lesson.title}`,
            html: emailHtml,
            user_id: user.id,
            cc: adminData?.email ? [adminData.email] : [],
          });

          if (emailResult.success) {
            console.log(
              "Cancellation confirmation email sent:",
              emailResult.message_id,
            );
          } else {
            console.error(
              "Failed to send cancellation confirmation email:",
              emailResult.error,
            );
          }

          // Create notification
          await notificationsApi.create({
            user_id: user.id,
            type: "lesson_cancelled",
            title: "Rezervácia lekcie zrušená",
            message: `Vaša rezervácia pre lekciu "${lesson.title}" bola zrušená. Dátum: ${lessonDate}, čas: ${lessonTime}`,
            data: {
              lesson_id: lesson.id,
              lesson_title: lesson.title,
              signup_id: signupId,
            },
            sendEmail: false, // Email už odoslaný cez emailApi
          });
        }
      }
      finish();
      return true;
    } catch (error) {
      set({ error: toErrorMessage(error) });
      finish();
      return false;
    }
  },

  /**
   * Odoslať požiadavku na súkromnú lekciu
   */
  requestPrivateLesson: async ({
    userId,
    startAt,
    durationHours,
    lessonType,
    pairId,
    availabilitySlotId,
    danceStyle,
    additionalNotes,
  }) => {
    set({ isLoading: true, error: null });
    const finish = () => set({ isLoading: false });
    try {
      const noteParts: string[] = [];

      if (danceStyle && danceStyle.trim().length > 0) {
        noteParts.push(`Tanečný štýl: ${danceStyle.trim()}`);
      }

      const durationLabel = Number.isInteger(durationHours)
        ? `${durationHours} h`
        : `${durationHours.toFixed(1)} h`;
      noteParts.push(`Trvanie: ${durationLabel}`);

      if (additionalNotes && additionalNotes.trim().length > 0) {
        noteParts.push(additionalNotes.trim());
      }

      const payload = {
        user_id: userId,
        start_at: startAt,
        duration_hours: durationHours,
        lesson_type: lessonType,
        ...(pairId ? { pair_id: pairId } : {}),
        ...(availabilitySlotId
          ? { availability_slot_id: availabilitySlotId }
          : {}),
        notes: noteParts.join("\n"),
      };

      const response = await lessonsApi.bookPrivate(payload);

      if (response.success && response.data) {
        // Refresh private lessons to keep UI up to date
        await get().fetchPrivateLessons(userId);

        // Send email notification to dancer and admin
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (user && user.email) {
          // Get user profile for display name
          const { data: userProfile } = await supabase
            .from("users")
            .select("display_name")
            .eq("id", user.id)
            .single();

          // Get admin email
          const { data: adminData } = await supabase
            .from("users")
            .select("email")
            .eq("role", "admin")
            .single();

          const userName =
            userProfile?.display_name || user.email.split("@")[0];
          const lessonDate = new Date(
            response.data.start_at,
          ).toLocaleDateString("sk-SK");
          const lessonTime = new Date(
            response.data.start_at,
          ).toLocaleTimeString("sk-SK", {
            hour: "2-digit",
            minute: "2-digit",
          });

          const emailHtml = getPrivateLessonRequestTemplate({
            userName: userName,
            lessonDate: lessonDate,
            lessonTime: lessonTime,
            durationHours: response.data.duration_hours,
            lessonType: response.data.lesson_type,
            danceStyle: danceStyle,
            notes: noteParts.join("\n"),
          });

          const emailResult = await emailApi.sendEmail({
            to: user.email,
            subject: "Požiadavka na súkromnú lekciu prijatá",
            html: emailHtml,
            user_id: user.id,
            cc: adminData?.email ? [adminData.email] : [],
          });

          if (emailResult.success) {
            console.log(
              "Private lesson request email sent:",
              emailResult.message_id,
            );
          } else {
            console.error(
              "Failed to send private lesson request email:",
              emailResult.error,
            );
          }

          // Create notification
          await notificationsApi.create({
            user_id: user.id,
            type: "lesson_reservation",
            title: "Požiadavka na súkromnú lekciu prijatá",
            message: `Vaša požiadavka na súkromnú lekciu bola prijatá. Dátum: ${lessonDate}, čas: ${lessonTime}, trvanie: ${response.data.duration_hours}h`,
            data: {
              lesson_id: response.data.id,
              lesson_date: response.data.start_at,
              lesson_type: response.data.lesson_type,
            },
            sendEmail: false, // Email už odoslaný cez emailApi
          });

          const n8nResult = await n8nApi.notifyPrivateLessonReservation({
            privateLesson: response.data,
            client: {
              id: user.id,
              name: userName,
              email: user.email,
            },
            reservation: {
              lessonDate,
              lessonTime,
              durationHours: response.data.duration_hours,
              lessonType: response.data.lesson_type,
              lessonTypeLabel:
                response.data.lesson_type === "pair"
                  ? "Párová lekcia"
                  : "Individuálna lekcia",
              danceStyle: danceStyle?.trim() || undefined,
              notes: noteParts.join("\n"),
            },
          });

          if (!n8nResult.success) {
            console.error(
              "Failed to send n8n private lesson notification:",
              n8nResult.error,
            );
          }
        }

        finish();
        return true;
      }

      const errMsg = response.error
        ? response.error
        : "Failed to request private lesson";
      set({ error: errMsg });
      finish();
      return false;
    } catch (error) {
      set({ error: toErrorMessage(error) });
      finish();
      return false;
    }
  },

  /**
   * Vyčistiť lesson data
   */
  clearLessons: () => {
    set({
      groupLessons: [],
      privateLessons: [],
      upcomingLessons: [],
      mySignups: [],
      selectedLesson: null,
      error: null,
    });
  },

  /**
   * Realtime: Handler pre zmeny v group_lessons
   */
  handleLessonChange: () => {
    // Refresh group lessons a upcoming lessons
    void get().fetchGroupLessons();
    void get().fetchUpcomingLessons();
  },

  /**
   * Realtime: Handler pre zmeny v group_signups
   */
  handleSignupChange: async () => {
    // Refresh signups a lessons (pre aktualizáciu kapacity)
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (user) {
      await get().fetchMySignups(user.id);
    }
    await get().fetchGroupLessons();
  },

  /**
   * Realtime: Handler pre zmeny v private_lessons
   */
  handlePrivateLessonChange: async () => {
    // Refresh private lessons
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (user) {
      await get().fetchPrivateLessons(user.id);
    }
  },
}));
